/**
 * Secure Token Manager
 * Provides secure token storage and management with fallback for page refresh
 */

interface TokenData {
  token: string;
  expiresAt?: number;
  refreshToken?: string;
}

class TokenManager {
  private static instance: TokenManager;
  private tokenData: TokenData | null = null;
  private isTabClosing: boolean = false;

  private constructor() {
    this.setupTabCloseDetection();
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Setup event listeners to detect tab close vs page refresh
   */
  private setupTabCloseDetection(): void {
    if (typeof window === 'undefined') return;

    // Detect if tab is actually closing (not just page refresh)
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Set flag to indicate tab is closing
      this.isTabClosing = true;
      
      // Clear sessionStorage on actual tab close
      try {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authUser');
      } catch (error) {
        console.warn('Failed to clear sessionStorage on tab close:', error);
      }
    };

    // Reset flag on page show (user came back to tab)
    const handlePageShow = () => {
      this.isTabClosing = false;
    };

    // Handle visibility change (tab switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this.isTabClosing = false;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }

  /**
   * Store token securely with fallback for page refresh
   */
  setToken(token: string, expiresIn?: number, refreshToken?: string): void {
    const tokenData: TokenData = {
      token,
      expiresAt: expiresIn ? Date.now() + expiresIn * 1000 : undefined,
      refreshToken
    };

    // Store in memory for immediate access
    this.tokenData = tokenData;

    // Store in sessionStorage (primary storage)
    try {
      sessionStorage.setItem('authToken', JSON.stringify(tokenData));
    } catch (error) {
      console.warn('Failed to store token in sessionStorage:', error);
    }

    // Store in localStorage as fallback for page refresh
    try {
      localStorage.setItem('authToken', JSON.stringify(tokenData));
    } catch (error) {
      console.warn('Failed to store token in localStorage:', error);
    }
  }

  /**
   * Get token from memory, sessionStorage, or localStorage fallback
   */
  getToken(): string | null {
    // Check if token is in memory
    if (this.tokenData?.token) {
      // Check if token is expired
      if (this.tokenData.expiresAt && Date.now() > this.tokenData.expiresAt) {
        console.log('Token expired, clearing');
        this.clearToken();
        return null;
      }
      return this.tokenData.token;
    }

    // Try to get from sessionStorage first
    try {
      const stored = sessionStorage.getItem('authToken');
      if (stored) {
        const tokenData: TokenData = JSON.parse(stored);
        
        // Check if token is expired
        if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
          console.log('Stored token expired, clearing');
          this.clearToken();
          return null;
        }

        // Restore to memory
        this.tokenData = tokenData;
        console.log('Token restored from sessionStorage');
        return tokenData.token;
      }
    } catch (error) {
      console.warn('Failed to retrieve token from sessionStorage:', error);
    }

    // Fallback to localStorage for page refresh scenarios
    try {
      const localStored = localStorage.getItem('authToken');
      if (localStored) {
        const tokenData: TokenData = JSON.parse(localStored);
        
        // Check if token is expired
        if (tokenData.expiresAt && Date.now() > tokenData.expiresAt) {
          console.log('Local stored token expired, clearing');
          this.clearToken();
          return null;
        }

        // Restore to memory and sessionStorage
        this.tokenData = tokenData;
        try {
          sessionStorage.setItem('authToken', localStored);
        } catch (error) {
          console.warn('Failed to sync token to sessionStorage:', error);
        }
        console.log('Token restored from localStorage (page refresh)');
        return tokenData.token;
      }
    } catch (error) {
      console.warn('Failed to retrieve token from localStorage:', error);
    }

    return null;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    if (!this.tokenData) {
      this.getToken();
    }
    return this.tokenData?.refreshToken || null;
  }

  /**
   * Check if token needs refresh (within 5 minutes of expiry)
   */
  needsRefresh(): boolean {
    if (!this.tokenData) {
      this.getToken();
    }
    if (!this.tokenData?.expiresAt) {
      return false;
    }
    
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return this.tokenData.expiresAt < fiveMinutesFromNow;
  }

  /**
   * Refresh token if needed
   */
  async refreshTokenIfNeeded(): Promise<boolean> {
    if (!this.needsRefresh()) {
      return true; // Token is still valid
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.log('No refresh token available');
      return false;
    }

    try {
      // Call refresh token API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const body = await response.json();
        const data = body?.data ?? body;
        this.setToken(data.authToken || data.token, data.expiresIn, data.refreshToken);
        return true;
      } else {
        console.log('Token refresh failed');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  /**
   * Check if token is valid and not expired
   */
  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    // Check expiration
    if (this.tokenData?.expiresAt && Date.now() > this.tokenData.expiresAt) {
      console.log('Token expired, clearing and redirecting to login');
      this.clearToken();
      this.clearUserData();
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return false;
    }

    return true;
  }

  /**
   * Clear user data from both storages
   */
  clearUserData(): void {
    try {
      sessionStorage.removeItem('authUser');
      localStorage.removeItem('authUser');
    } catch (error) {
      console.warn('Failed to clear user data:', error);
    }
  }

  /**
   * Clear all token data from both storages
   */
  clearToken(): void {
    this.tokenData = null;
    try {
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('authToken');
    } catch (error) {
      console.warn('Failed to clear tokens from storage:', error);
    }
  }

  /**
   * Store user data securely with fallback for page refresh
   */
  setUserData(userData: any): void {
    try {
      // Don't store sensitive data like tokens in user data
      const { authToken, ...safeUserData } = userData;
      
      // Ensure null values are preserved (not converted to strings)
      const processedUserData = {
        ...safeUserData,
        subRole: safeUserData.subRole === "null" ? null : safeUserData.subRole,
        role: safeUserData.role === "null" ? null : safeUserData.role
      };
      
      console.log('TokenManager.setUserData:', {
        original: userData,
        processed: processedUserData,
        isAdmin: processedUserData.subRole === null
      });
      
      // Store in sessionStorage (primary storage)
      sessionStorage.setItem('authUser', JSON.stringify(processedUserData));
      
      // Store in localStorage as fallback for page refresh
      localStorage.setItem('authUser', JSON.stringify(processedUserData));
    } catch (error) {
      console.warn('Failed to store user data:', error);
    }
  }

  /**
   * Get user data with fallback for page refresh
   */
  getUserData(): any | null {
    try {
      // Try sessionStorage first
      const stored = sessionStorage.getItem('authUser');
      if (stored) {
        const userData = JSON.parse(stored);
        // Ensure null values are properly restored
        const processedUserData = {
          ...userData,
          subRole: userData.subRole === "null" ? null : userData.subRole,
          role: userData.role === "null" ? null : userData.role
        };
        
        console.log('TokenManager.getUserData from sessionStorage:', {
          stored: userData,
          processed: processedUserData,
          isAdmin: processedUserData.subRole === null
        });
        
        return processedUserData;
      }
      
      // Fallback to localStorage for page refresh scenarios
      const localStored = localStorage.getItem('authUser');
      if (localStored) {
        const userData = JSON.parse(localStored);
        // Ensure null values are properly restored
        const processedUserData = {
          ...userData,
          subRole: userData.subRole === "null" ? null : userData.subRole,
          role: userData.role === "null" ? null : userData.role
        };
        
        console.log('TokenManager.getUserData from localStorage (page refresh):', {
          stored: userData,
          processed: processedUserData,
          isAdmin: processedUserData.subRole === null
        });
        
        // Sync to sessionStorage for future use
        try {
          sessionStorage.setItem('authUser', localStored);
        } catch (e) {
          console.warn('Failed to sync user data to sessionStorage:', e);
        }
        return processedUserData;
      }
      
      console.log('TokenManager.getUserData: No user data found');
      return null;
    } catch (error) {
      console.warn('Failed to retrieve user data:', error);
      return null;
    }
  }

  /**
   * Clear all authentication data from both storages
   */
  logout(): void {
    this.clearToken();
    this.clearUserData();
  }
}

export default TokenManager.getInstance(); 
